import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store';
import { login, register, logout } from '../store/actions';
import { clearError } from '../store/reducers/authSlice';
import type { LoginRequest, RegisterRequest } from '../types';
import { logoutUser } from '../utils/logout';


export const useAuth = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { data, isAuthenticated, loading, error } = useAppSelector(
    (state) => state.auth
  );

  /** 
   * Login handler
   * @param credentials - Email and password
   */
  const loginHandler = useCallback(async (credentials: LoginRequest) => {
    try {
      const result = await dispatch(login(credentials)).unwrap();
      return { success: true, data: result };
    } catch (err: any) {
      return { success: false, error: err };
    }
  }, [dispatch]);

  /**
   * Register handler
   * @param userData - Registration data
   */
  const registerHandler = useCallback(async (userData: RegisterRequest) => {
    try {
      await dispatch(register(userData)).unwrap();
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err };
    }
  }, [dispatch]);

  const logoutHandler = useCallback(async () => {
    try {
      await dispatch(logout()).unwrap();
      await logoutUser();
      navigate('/', { replace: true });
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err };
    }
  }, [dispatch, navigate]);

  const clearAuthError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  const checkAuth = useCallback(() => {
    return isAuthenticated && !!data;
  }, [data, isAuthenticated]);

  const getUserSpecialty = useCallback(() => {
    return data?.userId || null;
  }, [data?.userId]);

  const isProfileComplete = useCallback(() => {
    if (!data) return false;
    return !!(
      data.userId &&
      data.email &&
      data.token &&
      data.expiresAt
    );
  }, [data]);

  return useMemo(() => ({
    data,
    isAuthenticated,
    isLoading: loading,
    error,
    
    login: loginHandler,
    register: registerHandler,
    logout: logoutHandler,
    clearError: clearAuthError,
    
    checkAuth,
    getUserSpecialty,
    isProfileComplete,
  }), [
    checkAuth,
    clearAuthError,
    data,
    error,
    getUserSpecialty,
    isAuthenticated,
    isProfileComplete,
    loading,
    loginHandler,
    logoutHandler,
    registerHandler,
  ]);
};
